<template>
  <div class="payment-section mt-2">
    <div class="px-2">
      <p class="payment-section-title">Payment Summary</p>
    </div>
    <div class="payment-card">
      <div class="payment-row-top">
        <div>
          <p class="payment-amount">{{ totalAmountFormatted }}</p>
          <v-chip
            :color="isPaid ? 'success' : 'error'"
            size="small"
            label
          >
            {{ isPaid ? "Paid" : "Unpaid" }}
          </v-chip>
        </div>
        <div class="payment-meta" v-if="paymentCreatedAt">
          <p class="payment-method text-capitalize">{{ paymentTypeDisplay }} {{ paymentCardLast4Display }}</p>
          <p class="payment-time">{{ paymentCreatedAt ? formatDate(paymentCreatedAt) : "-" }}</p>
        </div>
      </div>
      <div class="payment-breakdown">
          <div class="breakdown-row" v-if="subTotalFormatted !== '-'">
            <span>Subtotal</span>
            <span>{{ subTotalFormatted }}</span>
          </div>
          <div class="breakdown-row" v-if="taxFormatted !== '-'">
            <span>Tax</span>
            <span>{{ taxFormatted }}</span>
          </div>
          <div class="breakdown-row breakdown-total">
            <span>Total</span>
            <span>{{ totalAmountFormatted }}</span>
          </div>
        </div>
      </div>

    <transition name="collapse">
      <div v-if="showDetails" class="payment-details">
        <div class="payment-row">
          <div class="payment-icon">
            <v-icon size="16">mdi-receipt-text-outline</v-icon>
          </div>
          <div>
            <p class="payment-label">Payment Code</p>
            <p class="payment-value">{{ paymentCode }}</p>
          </div>
        </div>

        <div class="payment-row">
          <div class="payment-icon">
            <v-icon size="16">mdi-credit-card-outline</v-icon>
          </div>
          <div>
            <p class="payment-label">Payment Method</p>
            <p class="payment-value">{{ paymentCardLast4Display }}</p>
          </div>
        </div>

        <div class="payment-row">
          <div class="payment-icon">
            <v-icon size="16">mdi-cash-multiple</v-icon>
          </div>
          <div>
            <p class="payment-label">Paid Amount</p>
            <p class="payment-value">{{ paidAmountFormatted }}</p>
          </div>
        </div>

        <div class="payment-breakdown">
          <div class="breakdown-row">
            <span>Subtotal</span>
            <span>{{ subTotalFormatted }}</span>
          </div>
          <div class="breakdown-row">
            <span>Tax</span>
            <span>{{ taxFormatted }}</span>
          </div>
          <div class="breakdown-row breakdown-total">
            <span>Total</span>
            <span>{{ totalAmountFormatted }}</span>
          </div>
        </div>

      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { formatCurrency, formatDate } from "@/shared/utils/utils";

type PaymentInfo = {
  id: number | string;
  payment_code: string;
  amount: string | number;
  gateway: string;
  payment_type: string;
  card_last4: string | null;
  cardholder_name: string | null;
  transaction_id: string | null;
  ref_no: string | null;
  created_at: string;
} | null;

type TotalInfo = {
  is_paid: number | boolean;
  sub_total: string | number | null;
  tax_amount: string | number | null;
  total_amount: string | number | null;
} | null;

const props = defineProps<{
  appointment: {
    payment_info: PaymentInfo;
    total_info: TotalInfo;
  };
}>();

const showDetails = ref(false);

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

const isPaid = computed(() => Boolean(props.appointment.total_info?.is_paid));

const paymentCode = computed(() => props.appointment.payment_info?.payment_code || "-");
const paymentCreatedAt = computed(() => props.appointment.payment_info?.created_at || "");
const paymentTypeDisplay = computed(() => props.appointment.payment_info?.payment_type || "-");

const paymentCardLast4Display = computed(() => {
  const last4 = props.appointment.payment_info?.card_last4;
  return last4 ? `•••• ${last4}` : "-";
});

const paidAmountFormatted = computed(() => {
  const n = asNumber(props.appointment.payment_info?.amount);
  return n === null ? "-" : formatCurrency(n);
});

const subTotalFormatted = computed(() => {
  const n = asNumber(props.appointment.total_info?.sub_total);
  return n === null ? "-" : formatCurrency(n);
});

const taxFormatted = computed(() => {
  const n = asNumber(props.appointment.total_info?.tax_amount);
  return n === null ? "-" : formatCurrency(n);
});

const totalAmountFormatted = computed(() => {
  const n = asNumber(props.appointment.total_info?.total_amount);
  return n === null ? "-" : formatCurrency(n);
});
</script>

<style scoped>
.payment-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.payment-card {
  padding: 16px;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.payment-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.payment-amount {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.payment-meta {
  text-align: right;
}

.payment-method {
  margin: 0;
  font-size: var(--font-size-default);
  color: #0f172a;
  font-weight: 500;
}

.payment-time {
  margin: 4px 0 0;
  font-size: 14px;
  color: #94a3b8;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.payment-icon {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #f1f5f9;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  color: #475569;
}

.payment-label {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.payment-value {
  margin: 2px 0 0;
  font-weight: 600;
  color: #0f172a;
}

.payment-breakdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #e2e8f0;
  padding-top: 12px;
}

.breakdown-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #475569;
  font-size: 14px;
}

.breakdown-total {
  color: #0f172a;
  font-weight: 700;
}

.payment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
}

.payment-btn--solid {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}
</style>
